import { Note } from '../../../../components/Note/Note';

# Querying a position not posted reason

## Operations

### positionNotPostedReason

The [`positionNotPostedReason` query] accepts a `reasonId`, included in the [`PositionNotPostedEvent`] and returns the reason a position was not posted to SEEK, along with any suggested remediation actions.

Query the `status`, `message`, and `callToAction` fields to clearly communicate the issue and recommended next steps to the hirer, as demonstrated in the [`PositionNotPosted`] use case.
You may also query `displaySeverityCode` to better tailor how the message is presented - for example, by styling errors more prominently than informational notices.

```graphql
query ($input: PositionNotPostedReasonInput!) {
  positionNotPostedReason(input: $input) {
    status
    message
    displaySeverityCode
    errors {
      field
      value
      message
    }
    callToAction
    timestamp
  }
}
```

```json
{
  "input": {
    "reasonId": "globalPublicTest:position:notPosted:1111114bZ6BZRUqUqZeo"
  }
}
```

```json
{
  "positionNotPostedReason": {
    "status": "Couldn't post",
    "message": "SEEK couldn't post this ad as there may be an issue with your account. Contact your SEEK account manager to resolve this.",
    "displaySeverityCode": "error",
    "errors": [
      {
        "field": "field-0",
        "value": "value-0",
        "message": "Example message for ACCOUNT_CREDIT_HOLD"
      }
    ],
    "callToAction": "ContactSeek",
    "timestamp": "2024-01-01T00:00:00.000Z"
  }
}
```

### positionNotPostedReasons

The [`positionNotPostedReasons` query] accepts a `seekPartnerPositionId`, included in the [`PositionNotPostedEvent`], along with the `seekAdSyncId` and optional `seekSubOrgReference` used when posting the position with the [`syncPositions` mutation].

It returns the 25 most recent historical reasons why a position was not posted to SEEK, along with any suggested remediation actions.

```graphql
query ($input: PositionNotPostedReasonsInput!) {
  positionNotPostedReasons(input: $input) {
    status
    message
    displaySeverityCode
    errors {
      field
      value
      message
    }
    callToAction
    timestamp
  }
}
```

```json
{
  "input": {
    "seekPartnerPositionId": "REQ-123",
    "seekAdSyncId": "seek-adSync-PlayGroundId",
    "seekSubOrgReference": "acme-melbourne"
  }
}
```

```json
{
  "positionNotPostedReasons": [
    {
      "status": "Couldn't edit",
      "message": "This ad wasn't updated because it was edited via a different method. To update it, edit the ad via the same method.",
      "displaySeverityCode": "warning",
      "errors": [
        {
          "field": "field-11",
          "value": "value-11",
          "message": "Example message for EDIT_BLOCKED"
        }
      ],
      "callToAction": null,
      "timestamp": "2024-01-12T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "SEEK couldn't post this ad as something went wrong. Try again, or contact SEEK if the problem continues.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-10",
          "value": "value-10",
          "message": "Example message for OTHER"
        }
      ],
      "callToAction": "ContactSeek",
      "timestamp": "2024-01-11T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "SEEK couldn't post this ad because salary details were missing and couldn't be worked out from the job details. Add salary information to the requisition and try again.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-9",
          "value": "value-9",
          "message": "Example message for SALARY_INFERENCE_FAILED"
        }
      ],
      "callToAction": "ManagePosition",
      "timestamp": "2024-01-10T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "SEEK couldn't post this ad because we couldn't find a matching job category. Update the job title or description in the requisition and try again.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-8",
          "value": "value-8",
          "message": "Example message for CATEGORY_INFERENCE_FAILED"
        }
      ],
      "callToAction": "ManagePosition",
      "timestamp": "2024-01-09T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "SEEK couldn't post this ad because the job location isn't supported by SEEK. Update the location in the requisition and try again.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-7",
          "value": "value-7",
          "message": "Example message for LOCATION_NON_SEEK_MARKET"
        }
      ],
      "callToAction": "ManagePosition",
      "timestamp": "2024-01-08T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "SEEK couldn’t post this ad because we couldn’t find a valid location in your job details. Update the requisition and try again.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-6",
          "value": "value-6",
          "message": "Example message for LOCATION_INFERENCE_FAILED"
        }
      ],
      "callToAction": "ManagePosition",
      "timestamp": "2024-01-07T00:00:00.000Z"
    },
    {
      "status": "Not posted",
      "message": "This ad wasn't posted on SEEK because it was excluded based on your business Ad Sync rules. To post it, contact SEEK to update your rules.",
      "displaySeverityCode": "info",
      "errors": [
        {
          "field": "field-5",
          "value": "value-5",
          "message": "Example message for EXCLUSION_RULE_APPLIED"
        }
      ],
      "callToAction": "ContactSeek",
      "timestamp": "2024-01-06T00:00:00.000Z"
    },
    {
      "status": "Not posted",
      "message": "This ad wasn't posted on SEEK because you included an #s-dni tag in the job description. To post it, edit the requisition to remove the tag.",
      "displaySeverityCode": "info",
      "errors": [
        {
          "field": "field-4",
          "value": "value-4",
          "message": "Example message for DNI_TAG_DETECTED"
        }
      ],
      "callToAction": "ManagePosition",
      "timestamp": "2024-01-05T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "SEEK couldn't post this ad because it's already expired.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-3",
          "value": "value-3",
          "message": "Example message for EXPIRED_AD"
        }
      ],
      "callToAction": "ContactSeek",
      "timestamp": "2024-01-04T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "We couldn't post this ad because it's not linked to a SEEK account. Contact your SEEK account manager to update your preferences.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-2",
          "value": "value-2",
          "message": "Example message for ACCOUNT_NOT_FOUND"
        }
      ],
      "callToAction": "ContactSeek",
      "timestamp": "2024-01-03T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "SEEK couldn't post this ad because it appears to be a duplicate.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-1",
          "value": "value-1",
          "message": "Example message for DUPLICATE_AD_DETECTED"
        }
      ],
      "callToAction": "ContactSeek",
      "timestamp": "2024-01-02T00:00:00.000Z"
    },
    {
      "status": "Couldn't post",
      "message": "SEEK couldn't post this ad as there may be an issue with your account. Contact your SEEK account manager to resolve this.",
      "displaySeverityCode": "error",
      "errors": [
        {
          "field": "field-0",
          "value": "value-0",
          "message": "Example message for ACCOUNT_CREDIT_HOLD"
        }
      ],
      "callToAction": "ContactSeek",
      "timestamp": "2024-01-01T00:00:00.000Z"
    }
  ]
}
```

<Note /> Requests will be routed to the Playground environment if `seekAdSyncId`
is `seek-adSync-PlayGroundId`

[`PositionNotPostedEvent`]: /schema/webhooks/PositionNotPostedEvent
[`PositionNotPosted`]: /use-cases/job-posting/ad-sync/display-job-ads#positionnotposted
[`positionNotPostedReason` query]: /schema/query/positionNotPostedReason
[`positionNotPostedReasons` query]: /schema/query/positionNotPostedReasons
[`syncPositions` mutation]: /use-cases/job-posting/ad-sync/sync-positions
